import { measureSms } from './smsDraft.js';
import { isLeadMessage } from './messageKinds.js';
import { type BotConversation, type BotMessage, type HandoffTriggerKind } from '../../types/leadBot.js';
import { CONVERSION_OUTCOMES, LEAD_OUTCOMES, type LeadOutcome } from '../../types/leadOutcome.js';

// SMS Lead Bot — the reporting aggregation. PURE, and every honesty rule in the
// dashboard lives here rather than in a template.
//
// ⚠️⚠️ FOUR RULES, AND THEY ARE THE POINT OF THIS FILE. A trial is decided on
// small numbers, which is exactly when a confident-looking figure does the most
// damage:
//
//   1. EVERY RATE CARRIES ITS DENOMINATOR. "67%" over three conversations is
//      not a finding, and a reader cannot tell the difference unless we show it.
//   2. A PERCENTAGE BELOW A SMALL FLOOR IS SUPPRESSED rather than printed. See
//      MIN_RATE_SAMPLE.
//   3. UNMEASURED IS NULL, NEVER ZERO. "Nobody has classified an outcome" and
//      "nobody booked" are different facts, and a 0 on a client-facing-ish page
//      reads as the second one.
//   4. ⚠️ REPLY RATE EXCLUDES CONVERSATIONS STILL IN FLIGHT. A conversation
//      started ten minutes ago has not failed to reply yet; counting it as a
//      non-reply understates the bot for the whole of the current day, and it
//      does so in a way that changes depending on when you look — which makes it
//      almost impossible to spot by eye.
//
// ⚠️ NO COLLECTION, NO COUNTERS. Everything here aggregates in memory from the
// conversation and message docs (the surveys / hosted-reports fetch-all
// posture). At ~100 leads a month a maintained counter buys nothing and is a
// thing that can drift away from the documents it claims to summarise. REVISIT
// TRIGGER: a few thousand conversations.

/**
 * ⚠️ Below this, a percentage is not reported at all. Deliberately not a
 * "confidence interval" — this is a trial dashboard, and the honest move is to
 * show the raw counts and withhold the ratio rather than dress up a small
 * sample with statistics.
 */
export const MIN_RATE_SAMPLE = 5;

/**
 * How long after the last outbound message a conversation is still considered
 * IN FLIGHT — i.e. the lead may yet reply.
 *
 * ⚠️ THE VALUE IS A JUDGEMENT AND THE MECHANISM IS NOT. Whether 24 hours is
 * right is arguable; that a conversation opened minutes ago must not count as a
 * non-reply is not. Erring long is the safe direction: it shrinks the reply-rate
 * denominator rather than inflating the failure count.
 */
export const IN_FLIGHT_WINDOW_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// shapes
// ---------------------------------------------------------------------------

/** A rate that knows how sure it is. ⚠️ `value` is null when unreportable. */
export interface Rate {
  /** The share, 0-1 — or null when the sample is below the floor. */
  value: number | null;
  numerator: number;
  denominator: number;
  /** True when the sample was too small to report a percentage. */
  suppressed: boolean;
}

export interface ComplianceHit {
  conversationId: string;
  leadName: string;
  accountName: string | null;
  /** What the detector saw, in words. */
  detail: string;
  /** Which layer caught it — our own rule, or the model's own classification. */
  source: 'rule' | 'model' | 'manual';
  at: string | null;
}

export interface CountByKey {
  key: string;
  label: string;
  count: number;
}

export interface DropOffPoint {
  /** Exchange number: 1 is the opener, 2 the first reply, and so on. */
  turn: number;
  /** Conversations that reached at least this many exchanges. */
  reached: number;
  /** Conversations whose LAST exchange was this one — i.e. they stopped here. */
  stopped: number;
}

export interface OutcomeBreakdown {
  outcome: LeadOutcome;
  count: number;
  /** ⚠️ Null below the floor, and computed over CLASSIFIED conversations only. */
  share: number | null;
}

export interface CostSummary {
  /** Conversations that carry at least one message with token data. */
  measuredConversations: number;
  /** ⚠️ The denominator. Messages predating token capture have none. */
  totalConversations: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** Billable SMS segments across every message actually sent. */
  smsSegments: number;
  /** ⚠️ Null when nothing is measured — never 0, which would read as "free". */
  tokensPerConversation: number | null;
  segmentsPerConversation: number | null;
}

export interface LeadBotReport {
  // tier 1 — is it safe?
  complianceHits: ComplianceHit[];
  handoffsByReason: CountByKey[];
  handoffCount: number;
  blocksByReason: CountByKey[];
  sendFailures: number;
  unreviewedSends: number;
  totalSends: number;

  // tier 2 — is it working?
  conversationsStarted: number;
  bySource: CountByKey[];
  byCampaign: CountByKey[];
  replyRate: Rate;
  inFlightExcluded: number;
  medianTurns: number | null;
  medianFirstReplySeconds: number | null;
  dropOff: DropOffPoint[];
  inboundByHour: number[];

  // tier 3 — outcomes
  outcomes: OutcomeBreakdown[];
  classifiedCount: number;
  unclassifiedCount: number;
  /** Committed + Sale (a call back booked, a tour booked, a join) over the classified conversations. */
  conversionRate: Rate;
  /** Sale only (joined), reported beside it so the two are never read as one figure. */
  joinedRate: Rate;

  cost: CostSummary;
}

/**
 * ⚠️⚠️ EXTEND A ROLLING WINDOW TO INCLUDE TODAY.
 *
 * The shared `presetToRange` ends every "past N days" window YESTERDAY,
 * deliberately: on the ad dashboards today is a partial day from a platform
 * still ingesting, and including it would make every window read low.
 *
 * ⚠️ THAT REASONING DOES NOT HOLD HERE. These are OUR OWN Firestore documents —
 * a conversation that started an hour ago is completely recorded, and it is
 * often the most interesting one on the page. Worse, `inFlightExcluded` is by
 * definition about the last 24 hours, so a window ending yesterday would make a
 * load-bearing honesty rule UNREACHABLE: it could never fire, and nobody would
 * see that it wasn't firing.
 *
 * ⚠️ THE DEFINITION IS REUSED AND ONLY THE ROLLING WINDOWS MOVE. Forking
 * `presetToRange` would give this page its own idea of what "last month" means,
 * which is how two surfaces start disagreeing.
 *
 * ⚠️⚠️ IT KEYS ON THE PRESET, NOT ON THE DATES, and that is the whole
 * correctness of it. An earlier draft extended any window ending before today —
 * which silently turned "last month" and "yesterday" into "…up to right now",
 * reporting a period nobody selected. Only `past_*` carries the
 * incomplete-today convention; every other preset ends exactly where the user
 * asked it to.
 */
export function includeToday(
  range: { from: string; to: string },
  preset: string,
  now: Date,
): { from: string; to: string } {
  if (!preset.startsWith('past_')) return range;
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return range.to >= today ? range : { ...range, to: today };
}

export interface ReportFilters {
  accountId: string | null;
  botConfigId: string | null;
  /** Inclusive ISO date, YYYY-MM-DD. */
  from: string;
  /** Inclusive ISO date, YYYY-MM-DD. */
  to: string;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function parseMs(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * ⚠️ SUPPRESSES rather than rounds. Below the floor the counts are still
 * returned — a reader can see "2 of 3" and judge for themselves — but no
 * percentage is produced for a template to print confidently.
 */
export function rate(numerator: number, denominator: number): Rate {
  if (denominator <= 0) {
    return { value: null, numerator, denominator, suppressed: true };
  }
  if (denominator < MIN_RATE_SAMPLE) {
    return { value: null, numerator, denominator, suppressed: true };
  }
  return { value: numerator / denominator, numerator, denominator, suppressed: false };
}

/** ⚠️ Median, not mean — a couple of runaway threads drag an average badly. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

export function filterConversationsForReport(
  conversations: readonly BotConversation[],
  filters: ReportFilters,
): BotConversation[] {
  const fromMs = Date.parse(`${filters.from}T00:00:00.000Z`);
  const toMs = Date.parse(`${filters.to}T23:59:59.999Z`);
  return conversations.filter((c) => {
    // ⚠️ A TEST lead (route-editor or direct, `intakeTest`) is never counted: it would inflate every denominator and
    // conversion figure with conversations nobody real had.
    if (c.intakeTest === true) return false;
    if (filters.accountId && c.accountId !== filters.accountId) return false;
    if (filters.botConfigId && c.botConfigId !== filters.botConfigId) return false;
    const started = parseMs(c.createdAt);
    // ⚠️ A conversation with no readable start date is EXCLUDED rather than
    // swept into whatever window happens to be open — it would silently change
    // every denominator on the page.
    if (started === null) return false;
    if (!Number.isNaN(fromMs) && started < fromMs) return false;
    if (!Number.isNaN(toMs) && started > toMs) return false;
    return true;
  });
}

/**
 * ⚠️ IN FLIGHT = the lead has not replied AND there has not yet been long
 * enough for them to. This is the exclusion the reply rate rests on.
 */
export function isInFlight(
  conversation: BotConversation,
  messages: readonly BotMessage[],
  nowMs: number,
): boolean {
  if (hasLeadReplied(messages)) return false;
  // Nothing has gone out yet, so nothing has failed to be replied to.
  const lastOut = parseMs(conversation.lastOutboundAt);
  if (lastOut === null) return true;
  return nowMs - lastOut < IN_FLIGHT_WINDOW_MS;
}

export function hasLeadReplied(messages: readonly BotMessage[]): boolean {
  return messages.some(isLeadMessage);
}

/** Exchanges that actually happened: sent outbound + received inbound. */
export function countTurns(messages: readonly BotMessage[]): number {
  return messages.filter(
    (m) => isLeadMessage(m) || (m.direction === 'outbound' && m.status === 'sent'),
  ).length;
}

// ---------------------------------------------------------------------------
// the report
// ---------------------------------------------------------------------------

const TRIGGER_LABELS: Record<HandoffTriggerKind, string> = {
  compliance: 'Compliance (licence / towing / weight)',
  human_requested: 'Asked for a person',
  price_negotiation: 'Price negotiation',
  finance: 'Finance',
  trade_in: 'Trade-in',
  complaint: 'Complaint',
  booking_ready: 'Ready to book',
  unknown_to_kb: 'Not in the knowledge base',
  stalled: 'Going in circles',
  manual: 'Flagged by a person',
  cancellation_billing: 'Cancellation / refund / billing',
  injury_medical: 'Injury / medical',
  legal: 'Legal',
  safety_incident: 'Safety or incident',
  media_corporate: 'Media / franchise / corporate',
  faq_handover: 'Club-marked handover question',
  distress: 'Possible distress',
  opt_out: 'Asked us to stop',
  price_unverified: 'Reply held - unlisted price',
  not_sure: 'Assistant not sure',
  reply_cap: 'Reply limit reached',
  hourly_cap: 'Hourly reply limit reached',
  drafting_error: "Assistant couldn't reply (error)",
  automated_counterparty: 'Auto-reply detected',
  booking_failed: "Booking didn't go through",
};

const BLOCK_LABELS: Record<string, string> = {
  runaway: 'Runaway cap hit',
  stopped: 'Lead had sent STOP',
  opted_out: 'Number opted out of SMS',
  not_permitted: 'Not allowlisted',
};

const OUTCOME_ORDER = LEAD_OUTCOMES;

export function buildLeadBotReport(input: {
  conversations: readonly BotConversation[];
  /** Every message for the conversations above, in any order. */
  messages: readonly BotMessage[];
  filters: ReportFilters;
  nowMs: number;
}): LeadBotReport {
  const conversations = filterConversationsForReport(input.conversations, input.filters);
  const ids = new Set(conversations.map((c) => c.id));

  // ⚠️ Messages are narrowed to the FILTERED conversations, so every panel on
  // the page describes the same set. A tile filtered and a list not filtered is
  // the failure this line prevents.
  const byConversation = new Map<string, BotMessage[]>();
  for (const message of input.messages) {
    if (!ids.has(message.conversationId)) continue;
    const bucket = byConversation.get(message.conversationId);
    if (bucket) bucket.push(message);
    else byConversation.set(message.conversationId, [message]);
  }
  for (const bucket of byConversation.values()) {
    bucket.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
  }
  const messagesFor = (id: string): BotMessage[] => byConversation.get(id) ?? [];
  const allMessages = [...byConversation.values()].flat();

  // ------------------------------------------------------------ tier 1 -----
  const complianceHits: ComplianceHit[] = [];
  const handoffCounts = new Map<string, number>();
  let handoffCount = 0;

  for (const conversation of conversations) {
    const triggers = conversation.handoffTriggers ?? [];
    if (triggers.length > 0) handoffCount += 1;
    for (const trigger of triggers) {
      handoffCounts.set(trigger.kind, (handoffCounts.get(trigger.kind) ?? 0) + 1);
      // ⚠️ THE HEADLINE OF THE WHOLE PAGE, and it is a LIST rather than a count
      // because the value in a trial is reading what the bot actually said. A
      // number alone tells you nothing you can act on.
      if (trigger.kind === 'compliance') {
        complianceHits.push({
          conversationId: conversation.id,
          leadName: conversation.leadName,
          accountName: conversation.accountName,
          detail: trigger.detail,
          source: trigger.source,
          at: conversation.handoffAt ?? conversation.createdAt ?? null,
        });
      }
    }
  }
  complianceHits.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));

  const blockCounts = new Map<string, number>();
  let sendFailures = 0;
  let unreviewedSends = 0;
  let totalSends = 0;
  for (const message of allMessages) {
    if (message.blockedReason) {
      blockCounts.set(message.blockedReason, (blockCounts.get(message.blockedReason) ?? 0) + 1);
    }
    if (message.status === 'failed') sendFailures += 1;
    if (message.status === 'sent') {
      totalSends += 1;
      // ⚠️ Derived from the ABSENCE of an approver, which is the ground truth —
      // the same derivation the transcript uses, so the two cannot disagree.
      if (message.approvedBy === null) unreviewedSends += 1;
    }
  }

  // ------------------------------------------------------------ tier 2 -----
  const sourceCounts = new Map<string, number>();
  const campaignCounts = new Map<string, number>();
  const turnCounts: number[] = [];
  const firstReplySeconds: number[] = [];
  const inboundByHour = new Array<number>(24).fill(0);

  let replied = 0;
  let replyEligible = 0;
  let inFlightExcluded = 0;

  for (const conversation of conversations) {
    const messages = messagesFor(conversation.id);

    sourceCounts.set(conversation.source, (sourceCounts.get(conversation.source) ?? 0) + 1);
    const campaign = conversation.campaign?.trim() || 'No campaign';
    campaignCounts.set(campaign, (campaignCounts.get(campaign) ?? 0) + 1);

    const turns = countTurns(messages);
    if (turns > 0) turnCounts.push(turns);

    // ⚠️⚠️ THE EXCLUSION. A conversation opened ten minutes ago has not failed
    // to reply yet. Counting it as a non-reply understates the bot for the whole
    // current day and does so differently depending on when you look.
    if (isInFlight(conversation, messages, input.nowMs)) {
      inFlightExcluded += 1;
    } else {
      replyEligible += 1;
      if (hasLeadReplied(messages)) replied += 1;
    }

    // Time from the lead's first inbound to OUR next outbound send.
    const firstInbound = messages.find(isLeadMessage);
    if (firstInbound) {
      const inboundMs = parseMs(firstInbound.createdAt);
      const reply = messages.find(
        (m) =>
          m.direction === 'outbound' &&
          m.status === 'sent' &&
          (parseMs(m.sentAt) ?? 0) > (inboundMs ?? 0),
      );
      const replyMs = parseMs(reply?.sentAt ?? null);
      if (inboundMs !== null && replyMs !== null && replyMs >= inboundMs) {
        firstReplySeconds.push((replyMs - inboundMs) / 1000);
      }
    }

    for (const message of messages) {
      if (!isLeadMessage(message)) continue;
      const ms = parseMs(message.createdAt);
      if (ms === null) continue;
      const hour = new Date(ms).getHours();
      inboundByHour[hour] = (inboundByHour[hour] ?? 0) + 1;
    }
  }

  // ⚠️ DROP-OFF BY TURN tells you WHERE the bot loses people, not just that it
  // does — which is the difference between a number and something to act on.
  const maxTurns = turnCounts.length > 0 ? Math.max(...turnCounts) : 0;
  const dropOff: DropOffPoint[] = [];
  for (let turn = 1; turn <= Math.min(maxTurns, 12); turn += 1) {
    dropOff.push({
      turn,
      reached: turnCounts.filter((t) => t >= turn).length,
      stopped: turnCounts.filter((t) => t === turn).length,
    });
  }

  // ------------------------------------------------------------ tier 3 -----
  const outcomeCounts = new Map<LeadOutcome, number>();
  for (const conversation of conversations) {
    const outcome = conversation.outcome ?? 'unresolved';
    outcomeCounts.set(outcome, (outcomeCounts.get(outcome) ?? 0) + 1);
  }
  const unclassifiedCount = outcomeCounts.get('unresolved') ?? 0;
  const classifiedCount = conversations.length - unclassifiedCount;

  const outcomes: OutcomeBreakdown[] = OUTCOME_ORDER.map((outcome) => {
    const count = outcomeCounts.get(outcome) ?? 0;
    return {
      outcome,
      count,
      // ⚠️ SHARE OF CLASSIFIED, not of everything — and suppressed below the
      // floor. A share over all conversations would move every time someone
      // classified one, which reads as the bot getting worse.
      share:
        outcome === 'unresolved' ? null : rate(count, classifiedCount).value,
    };
  });

  const bookedCount = CONVERSION_OUTCOMES.reduce(
    (sum, outcome) => sum + (outcomeCounts.get(outcome) ?? 0),
    0,
  );

  const joinedCount = outcomeCounts.get('joined') ?? 0;

  // ------------------------------------------------------------- cost ------
  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let cacheWriteTokens = 0;
  let smsSegments = 0;
  const measuredIds = new Set<string>();

  for (const message of allMessages) {
    if (message.usage) {
      measuredIds.add(message.conversationId);
      inputTokens += message.usage.inputTokens ?? 0;
      outputTokens += message.usage.outputTokens ?? 0;
      cacheReadTokens += message.usage.cacheReadTokens ?? 0;
      cacheWriteTokens += message.usage.cacheWriteTokens ?? 0;
    }
    // ⚠️ SEGMENTS ARE DERIVED FROM THE STORED TEXT rather than stored alongside
    // it — one definition (`measureSms`, the same one the approval queue shows),
    // so the report and the composer cannot disagree about what a message cost.
    if (message.direction === 'outbound' && message.status === 'sent') {
      smsSegments += measureSms(message.text).segments;
    }
  }

  const measuredConversations = measuredIds.size;
  const cost: CostSummary = {
    measuredConversations,
    totalConversations: conversations.length,
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
    smsSegments,
    // ⚠️ NULL, NOT ZERO, when nothing is measured. A zero here reads as "free",
    // which is the single most misleading thing this panel could say.
    tokensPerConversation:
      measuredConversations > 0
        ? (inputTokens + outputTokens + cacheReadTokens + cacheWriteTokens) / measuredConversations
        : null,
    segmentsPerConversation:
      conversations.length > 0 ? smsSegments / conversations.length : null,
  };

  return {
    complianceHits,
    handoffsByReason: toCounts(handoffCounts, (k) => TRIGGER_LABELS[k as HandoffTriggerKind] ?? k),
    handoffCount,
    blocksByReason: toCounts(blockCounts, (k) => BLOCK_LABELS[k] ?? k),
    sendFailures,
    unreviewedSends,
    totalSends,

    conversationsStarted: conversations.length,
    bySource: toCounts(sourceCounts, (k) => k.replace(/_/g, ' ')),
    byCampaign: toCounts(campaignCounts, (k) => k),
    replyRate: rate(replied, replyEligible),
    inFlightExcluded,
    medianTurns: median(turnCounts),
    medianFirstReplySeconds: median(firstReplySeconds),
    dropOff,
    inboundByHour,

    outcomes,
    classifiedCount,
    unclassifiedCount,
    conversionRate: rate(bookedCount, classifiedCount),
    joinedRate: rate(joinedCount, classifiedCount),

    cost,
  };
}

function toCounts(counts: Map<string, number>, label: (key: string) => string): CountByKey[] {
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: label(key), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
